import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import DataTable from "../../utils/components/DataTable";
import Breadcrumbs from "../../utils/components/Breadcrumbs";
import Button from "../../utils/components/Button";
import Modal from "../../utils/components/Modal";
import SelectBox from "../../utils/components/SelectBox";
import Input from "../../utils/components/Input";
import Loading from "../../utils/components/Loading";
import {
  AiOutlinePlusCircle,
  AiOutlineSearch,
  AiFillEye,
  AiFillLock,
  AiOutlineExport,
} from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import { BiFilterAlt } from "react-icons/bi";
import { MdOutlineArrowForwardIos, MdLocationOn } from "react-icons/md";
import { auth } from "../../App/routes";
import { useProject } from "../../utils/hooks/useProject";
import API from "../../utils/API";
import { Translate } from "react-localize-redux";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../utils/components/AlertModal";
import { useLocation } from "react-router";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../utils/hooks/useCan";
import toastr from "toastr";
import NewTaskAssignmentModal from "./components/NewTaskAssignmentModal";
import ExportModal from "./components/ExportModal";

import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  getUnixTime,
  addDays
} from "date-fns";
import getTimezone from "../../utils/helpers/getTimezone";
import { RequestContext } from "../../utils/context/RequestContext";
import Conditions from "./components/Conditions";
import DateTimeLayout from "./components/DateTimeLayout";
import AutomaticFormCreationModal from "./components/AutomaticFormCreationModal";
import { getEnv } from "../../utils/env";

const columns = [
  {
    name: "#",
    selector: (row) => row.id,
    sortable: true,
    minWidth: "50px",
    sortField: "id",
  },
  {
    name: <Translate id="forms.headers.form_name" />,
    selector: (row) => row.formName,
    sortable: true,
    minWidth: "330px",
    cell: (row) => (
      <div
        className="row-status"
        data-status={row.status}
        data-tag="allowRowEvents"
      >
        <span data-tag="allowRowEvents">{row.no}</span>
        <br />
        <span className="text-gray" data-tag="allowRowEvents">
          {row.formName}
        </span>
      </div>
    ),
    sortField: "name",
  },
  {
    name: <Translate id="forms.uppercase_translates.status" />,
    selector: (row) => row.status,
    sortable: true,
    compact: true,
    sortField: "status",
  },
  {
    name: <Translate id="forms.uppercase_translates.created_by" />,
    selector: (row) => row.createdBy,
    sortable: true,
    compact: true,
    sortField: "created_by",
  },
  {
    name: <Translate id="forms.uppercase_translates.location" />,
    selector: (row) => row.location,
    sortable: true,
    minWidth: "330px",
    sortField: "location",
  },
  {
    name: <Translate id="forms.uppercase_translates.modified_on" />,
    selector: (row) => row.modifiedOn,
    format: (row) => timeStampToDateFormat(row.modifiedOn, "dd/MM/yyyy HH:mm"),
    sortable: true,
    sortField: "modified_on",
  },
  {
    name: <Translate id="forms.uppercase_translates.revision" />,
    selector: (row) => row.revision,
    sortable: true,
    compact: true,
    sortField: "revision",
  },
  {
    name: <Translate id="forms.uppercase_translates.actions" />,
    selector: (row) => row.actions,
    sortable: false,
  },
];

const filterFields = [
  {
    key: "formNo",
    label: <Translate id="forms.headers.form_no" />,
    type: "text",
    conditions: [
      "equal",
      "contains",
      "starts_with",
      "ends_with",
      "does_not_contain",
      "does_not_starts_with",
      "does_not_ends_with",
    ],
    placeholder: "forms.place-holders.form_no"
  },
  {
    key: "formName",
    label: <Translate id="forms.headers.form_name" />,
    type: "text",
    conditions: [
      "equal",
      "contains",
      "starts_with",
      "ends_with",
      "does_not_contain",
      "does_not_starts_with",
      "does_not_ends_with",
    ],
    placeholder: "forms.place-holders.form_name"
  },
  {
    key: "status",
    label: <Translate id="forms.headers.status" />,
    type: "select",
    conditions: [
      "equal",
      "not_qual",
    ],
    placeholder: "forms.place-holders.select-status",
    options: "form_status",
    isMulti: true
  },
  {
    key: "createdByCompanies",
    label: <Translate id="forms.headers.created_by_companies" />,
    type: "select",
    conditions: [
      "equal",
      "not_qual",
    ],
    placeholder: "forms.place-holders.select-created-by-companies",
    options: "created_by",
    isMulti: true
  },
  {
    key: "createdByUsers",
    label: <Translate id="forms.headers.created_by_users" />,
    type: "select",
    conditions: [
      "equal",
      "not_qual",
    ],
    placeholder: "forms.place-holders.select-created-by-users",
    options: "created_by_users",
    isMulti: true
  },
  {
    key: "createdAt",
    label: <Translate id="forms.headers.created_at" />,
    type: "datetime",
    conditions: [
      "today",
      "equal",
      "this_week",
      "this_month",
      "before",
      "after",
      "in_between",
    ],
    placeholder: "forms.place-holders.created_at"
  },
  {
    key: "updatedAt",
    label: <Translate id="forms.headers.updated_at" />,
    type: "datetime",
    conditions: [
      "today",
      "equal",
      "this_week",
      "this_month",
      "before",
      "after",
      "in_between",
    ],
    placeholder: "forms.place-holders.updated-at"
  }
];

const SEARCH_TIMEOUT = 800;

const Forms = ({ translate, language }) => {
  const { setRequestLoading } = useContext(RequestContext);

  const [formsList, setFormsList] = useState([]);
  const [siteSetId, setSiteSetId] = useState(null);
  const [siteSetDetailId, setSiteSetDetailId] = useState(null);
  const [locationSet, setLocationSet] = useState([]);
  const [locationSetRoot, setLocationSetRoot] = useState(null);
  const [selectedLocationSet, setSelectedLocationSet] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [filters, setFilters] = useState({
    attributes: [],
    created_by: [],
    created_by_users: [],
    form_status: [],
  });
  const [selectedFilters, setSelectedFilters] = useState({
    attributes: null,
    created_by: [],
    created_by_users: [],
    form_status: [],
  });

  const [advancedFilters, setAdvancedFilters] = useState({
    formNo: {
      condition: "contains",
      value: ""
    },
    formName: {
      condition: "contains", 
      value: ""
    },
    status: {
      condition: "equal",
      value: []
    },
    createdByCompanies: {
      condition: "equal",
      value: []
    },
    createdByUsers: {
      condition: "equal",
      value: []
    },
    createdAt: {
      condition: "equal",
      start_date: "",
      end_date: ""
    },
    updatedAt: {
      condition: "equal", 
      start_date: "",
      end_date: ""
    }
  });

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(
    localStorage.getItem("forms-per-page")
      ? parseInt(localStorage.getItem("forms-per-page"))
      : 10
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [allSiteSets, setAllSiteSets] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [tempSelectedAttributes, setTempSelectedAttributes] = useState([]);
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [showAutomaticFormCreationModal, setShowAutomaticFormCreationModal] = useState(false);
  const [viewNext, setViewNext] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectControl, setIsProjectControl] = useState(true);
  const [isLoadingSiteSetDetails, setIsLoadingSiteSetDetails] = useState(true);
  const [isEnableNewForm, setIsEnableNewForm] = useState(false);
  const [isLoadingFormTemplates, setIsLoadingFormTemplates] = useState(false);

  const [fetchedWithLocalLocations, setFetchedWithLocalLocations] =
    useState(false);

  const [showExportModal, setShowExportModal] = useState(false);

  const [selectedForms, setSelectedForms] = useState([]);
  const [selectedFormsLocation, setSelectedFormsLocation] = useState("");
  const [clearSelectedForms, setClearSelectedForms] = useState(false);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskModalData, setTaskModalData] = useState(null);

  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [hasLastSelectedSiteSetDetail, setHasLastSelectedSiteSetDetail] =
    useState(false);

  const [sortQuery, setSortQuery] = useState({
    sort_by: "id",
    order_by: "desc",
  });

  const [isFetched, setIsFetched] = useState(false);
  const [firstFetch, setFirstFetch] = useState(false);

  const { project } = useProject();
  const location = useLocation();

  const can = useCan();

  useEffect(() => {
    localStorage.removeItem("new_form_location_sets");

    const selectedFormsLocationRoot = localStorage.getItem(
      "selected-forms-location-root"
    );

    if (selectedFormsLocationRoot) {
      setLocationSetRoot(JSON.parse(selectedFormsLocationRoot));
    } else {
      setFetchedWithLocalLocations(true);
      return;
    }

    const selectedLocationSets = localStorage.getItem("selected-location-sets");

    if (selectedLocationSets) {
      setSelectedLocationSet(JSON.parse(selectedLocationSets));
    }

    if (selectedFormsLocationRoot && selectedLocationSets !== "[]") {
      getSiteSetByID(JSON.parse(selectedFormsLocationRoot).value, false);

      let lastSelectedLocationSet = JSON.parse(selectedLocationSets);
      if (lastSelectedLocationSet?.length)
        lastSelectedLocationSet =
          lastSelectedLocationSet[lastSelectedLocationSet.length - 1];

      if (lastSelectedLocationSet) {
        getSiteSetDetailByID(lastSelectedLocationSet.id);
        setHasLastSelectedSiteSetDetail(true);

        if (lastSelectedLocationSet?.is_details) setViewNext(true);
        else setViewNext(false);
      } else {
        setViewNext(true);
      }

      setIsFetched(false);
      getForms(perPage, 1);
    } else if (selectedFormsLocationRoot) {
      getSiteSetByID(JSON.parse(selectedFormsLocationRoot).value, true);
      setViewNext(true);

      setIsFetched(false);
      getForms(perPage, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFormsLocation && taskModalData === null)
      API.getData(`task/initialize?project_id=${project?.id}`).then(
        ({ data: res }) => {
          const users = res.data.users.map((user) => ({
            value: user.user_id,
            label: user.user_name,
          }));

          const companies = res.data.companies.map((company) => ({
            value: company.id,
            label: company.name,
            users: company.users,
          }));

          const groups = res.data.groups.map((group) => ({
            value: group.id,
            label: group.name,
            users: group.users,
          }));

          const taskModalData = {
            users,
            companies,
            groups,
            location,
          };

          setTaskModalData(taskModalData);
        }
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormsLocation, taskModalData]);

  const selectedAttributesControl = (id) => {
    return tempSelectedAttributes.find((item) => item.id === id);
  };

  const removeSelectedAttribute = (id) => {
    setSelectedAttributes(selectedAttributes.filter((item) => item.id !== id));
    setTempSelectedAttributes(
      tempSelectedAttributes.filter((item) => item.id !== id)
    );
  };

  const handleAttributesClick = (detail) => {
    const isSelected = selectedAttributesControl(detail.id);
    let updatedSelectedList = [];
    if (isSelected) {
      updatedSelectedList = tempSelectedAttributes.filter(
        (t) => t.id !== detail.id
      );
    } else {
      updatedSelectedList = [...tempSelectedAttributes, detail];
    }

    setTempSelectedAttributes(updatedSelectedList);
  };

  const handleSearch = (e) => {
    const query = e.target.value;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(() => {
        setQuery(query);
      }, SEARCH_TIMEOUT)
    );
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setResetPaginationToggle(!resetPaginationToggle);
    getForms(newPerPage, page, true);
    localStorage.setItem("forms-per-page", newPerPage);
  };

  const handlePageChange = (page) => {
    getForms(perPage, page, true);
  };


  const resetAdvancedFilters = () => {
    const resetConditions = {};
    advancedFilters.forEach(field => {
      resetConditions[field.key] = {
        condition: field.conditions[0],
        value: field.type === "select" ? [] : "",
        ...(field.type === "datetime" && { start_date: "", end_date: "" })
      };
    });
    setAdvancedFilters(resetConditions);
  };

  const handleModalApply = () => {
    setSelectedAttributes(tempSelectedAttributes);
    setSelectedFilters({
      ...selectedFilters,
      attributes: tempSelectedAttributes,
    });
    setSelectedFilters(prev => ({
      ...prev,
      conditions: advancedFilters
    }));
    setShow(!show);
    
    // Reset pagination and fetch forms with new filters
    setResetPaginationToggle(!resetPaginationToggle);
    setIsFetched(false);
    getForms(perPage, 1, true);
  };

  const handleModalClose = () => {
    resetAdvancedFilters();
    setShow(false);
  };

  const selectedLocationSetHandler = (index) => {
    let parentId = null;
    let newSelectedLocationSet = [];

    if (index > 0) {
      parentId = selectedLocationSet[index - 1].id;
      newSelectedLocationSet = selectedLocationSet.slice(0, index);

      getSiteSetDetailByID(parentId);
    } else if (index === 0) {
      parentId = locationSetRoot.value;
      newSelectedLocationSet = [];
      setSiteSetDetailId("");
      getSiteSetByID(parentId);
    }
    setViewNext(true);
    setSelectedLocationSet(newSelectedLocationSet);
    localStorage.setItem(
      "selected-location-sets",
      JSON.stringify(newSelectedLocationSet)
    );
  };

  const allSiteSetsHandler = (siteSet) => {
    setLocationSetRoot(siteSet);
    localStorage.setItem(
      "selected-forms-location-root",
      JSON.stringify(siteSet)
    );

    setSiteSetDetailId("");
    getSiteSetByID(siteSet.value);
    setViewNext(true);
  };

  const getAllSiteSetsByProjectID = () => {
    API.getData(`site_sets?project_id=${project.id}`).then(({ data: res }) => {
      setAllSiteSets(res.data);
    });
  };

  const getSiteSetByID = (id, setLocation = true) => {
    if (setLocation) setSelectedLocationSet([]);
    setIsLoadingSiteSetDetails(true);

    setIsEnableNewForm(false);
    API.getData(`site_set/${id}`).then(({ data: res }) => {
      if (setLocation) setLocationSet(res.data.details);
      setSiteSetId(res.data.id);

      setIsLoadingSiteSetDetails(false);
    });
  };

  const getSiteSetDetailByID = (id) => {
    setIsLoadingSiteSetDetails(true);

    setIsEnableNewForm(false);
    API.getData(`site_set_detail/${id}`).then(({ data: res }) => {
      setLocationSet(res.data.details);
      setSiteSetDetailId(res.data.id);
      setIsLoadingSiteSetDetails(false);
    });
  };

  const setSiteSetDetailByID = (id) => {
    let siteSet = locationSet.find((item) => item.id === id);
    const newSelectedLocationSet = [...selectedLocationSet, siteSet];
    setSelectedLocationSet(newSelectedLocationSet);
    localStorage.setItem(
      "selected-location-sets",
      JSON.stringify(newSelectedLocationSet)
    );

    if (!siteSet.is_details) {
      setViewNext(false);
    }
    getSiteSetDetailByID(id);
  };

  const getFormsFilterTable = () => {
    API.postData("/forms/filter_table", {
      project_id: project.id,
      site_set_id: siteSetId,
      site_set_detail_id: siteSetDetailId,
    }).then(({ data: res }) => {
      setFilters(res.data);
    });
  };

  const getFormsTemplateList = () => {
    if (!can.doThat("form:create") || !selectedLocationSet?.length)
      return false;

    setIsLoadingFormTemplates(true);
    API.getFormTemplates(project?.id, 1, 1000, null, true, {
      attributes: selectedAttributes.map((item) => item.id),
      site_set_id: siteSetId,
      site_set_level: selectedLocationSet?.length,
      site_set_detail_id: siteSetDetailId,
    }).then(({ data: res }) => {
      setFormTemplates(res.data);
      setIsEnableNewForm(true);
    }).finally(() => {
      setIsLoadingFormTemplates(false);
    });
  };

  const setFormsData = (formsData) => {
    let tableList = [];
    formsData.forEach((item) => {
      tableList.push({
        id: item.id,
        no: item.no,
        formName: item.name,
        status: item.status_set_detail_name,
        createdBy: item.created_name,
        location: item.location_path,
        modifiedOn: item.modified_at,
        revision: item.revision_set_detail_name,
        locked: item.locked,
        actions: (
          <div className="d-flex justify-content-center">
            <Link
              to={`/forms/${item.id}`}
              state={{
                from: location,
              }}
            >
              <AiFillEye className="fs-5" />
            </Link>
            <AiFillLock
              className={`fs-5 ms-3 ${!item.locked ? `d-none` : ""}`}
            />
          </div>
        ),
      });
    });
    setFormsList(tableList);
  };

  const checkDateFilterForBackend = (filter) => {
    let start_date;
    let end_date;
    const now = new Date();

    if (filter.condition === "before" || filter.condition === "after") {
      start_date = filter.start_date;
    } else if (filter.condition === "equal") {
      start_date = getUnixTime(startOfDay(new Date(filter.start_date) * 1000));
      end_date = getUnixTime(endOfDay(new Date(filter.start_date) * 1000));
    } else if (filter.condition === "today") {
      start_date = getUnixTime(startOfDay(now));
      end_date = getUnixTime(endOfDay(now));
    } else if (filter.condition === "this_week") {
      start_date = getUnixTime(startOfWeek(now, { weekStartsOn: 1 }));
      end_date = getUnixTime(endOfWeek(now, { weekStartsOn: 1 }));
    } else if (filter.condition === "this_month") {
      start_date = getUnixTime(startOfMonth(now));
      end_date = getUnixTime(endOfMonth(now));
    } else if (filter.condition === "in_between") {
      if (filter.start_date && filter.end_date) {
        start_date = filter.start_date;
        end_date = filter.end_date;
      } else {
        // console.error("In between condition value is required");
        // toastr.error("In between condition value is required");
        return undefined;
      }
    }

    if (!start_date && !end_date) {
      return undefined;
    }

    return {
      start_date: start_date,
      end_date: end_date
    };
  }

  const checkAdvancedFilters = () => {
    if (advancedFilters.createdAt.condition === "in_between" && advancedFilters.createdAt.start_date !== "" && !advancedFilters.createdAt.end_date) {
      return true;
    }
    if (advancedFilters.updatedAt.condition === "in_between" && advancedFilters.updatedAt.start_date !== "" && !advancedFilters.updatedAt.end_date) {
      return true;
    }
    return false;
  }

  const getForms = (newPerPage, page, forceUpdate = false) => {
    if (
      (isLoading || isFetched || (!locationSetRoot && !locationSet?.length)) &&
      !forceUpdate
    )
      return;

    setIsLoading(true);
    setRequestLoading(true);

    let url = `forms/filters?page=${page}&size=${newPerPage}&es=true`;

    if (query) {
      url += `&search=${query}`;
    }

    if (Object.keys(sortQuery).length) {
      url += `&${Object.keys(sortQuery)
        .map((key) => `${key}=${sortQuery[key]}`)
        .join("&")}`;
    }

    // Get 'set values' from local storage
    const storageLocationRoot = JSON.parse(
      localStorage.getItem("selected-forms-location-root")
    );

    const storageSiteSetDetail = JSON.parse(
      localStorage.getItem("selected-location-sets")
    )?.pop();

    const payload = {
      project_id: project.id,
      site_set_id: siteSetId ?? storageLocationRoot?.value,
      site_set_detail_id: siteSetDetailId ?? storageSiteSetDetail?.id,
      attributes: selectedAttributes.map((item) => item.id),
      created_bys: selectedFilters.created_by?.value
        ? [selectedFilters.created_by.value]
        : [],
      statuses: selectedFilters.form_status?.length
        ? selectedFilters.form_status?.map((item) => item.value)
        : [],

      advanced_filters: {
        form_no: advancedFilters.formNo.value !== "" ? advancedFilters.formNo : undefined,
        form_name: advancedFilters.formName.value !== "" ? advancedFilters.formName : undefined,
        statuses: advancedFilters.status.value.length > 0 ? { condition: advancedFilters.status.condition, value: advancedFilters.status.value.map((item) => item.value) } : undefined,
        created_by_companies: advancedFilters.createdByCompanies.value.length > 0 ? { condition: advancedFilters.createdByCompanies.condition, value: advancedFilters.createdByCompanies.value.map((item) => item.value) } : undefined,
        created_by_users: advancedFilters.createdByUsers.value.length > 0 ? { condition: advancedFilters.createdByUsers.condition, value: advancedFilters.createdByUsers.value.map((item) => item.value) } : undefined,
        created_at: checkDateFilterForBackend(advancedFilters.createdAt) !== undefined ? {condition: advancedFilters.createdAt.condition, ...checkDateFilterForBackend(advancedFilters.createdAt) } : undefined,
        updated_at: checkDateFilterForBackend(advancedFilters.updatedAt) !== undefined ? {condition: advancedFilters.updatedAt.condition, ...checkDateFilterForBackend(advancedFilters.updatedAt) } : undefined,
      }
    };

    API.postData(url, payload)
      .then(({ data: res }) => {
        setPerPage(newPerPage);
        setTotalRows(res.pagination.total_count);
        setCurrentPage(res.pagination.current_page);
        setFormsData(res.data);

        setIsLoading(false);

        setFetchedWithLocalLocations(true);

        setIsFetched(true);
      })
      .catch(() => {
        setFormsData([]);
        setIsLoading(false);
        setIsFetched(true);
      })
      .finally(() => {
        if (!firstFetch) setFirstFetch(true);
      });
  };

  useEffect(() => {
    if (project) {
      setIsProjectControl(false);
      getAllSiteSetsByProjectID();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (project) {
      getFormsFilterTable();
      getFormsTemplateList();
      setIsFetched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSetDetailId, siteSetId]);

  useEffect(() => {
    if (project && !show && fetchedWithLocalLocations) {
      setResetPaginationToggle(!resetPaginationToggle);
      setIsFetched(false);
      getFormsTemplateList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttributes, selectedFilters, query]);

  useEffect(() => {
    if (project && siteSetId) {
      if (!siteSetDetailId && hasLastSelectedSiteSetDetail) return;

      setResetPaginationToggle(!resetPaginationToggle);
      setIsFetched(false);

      if (!fetchedWithLocalLocations) getForms(perPage, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSetId, siteSetDetailId]);

  useEffect(() => {
    if (!show) {
      setTempSelectedAttributes(selectedAttributes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (project && sortQuery?.order_by && sortQuery?.sort_by) {
      getForms(perPage, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortQuery]);

  const handleSort = (column, direction) => {
    if (column?.sortField && direction) {
      setIsFetched(false)
      setSortQuery({
        sort_by: column.sortField,
        order_by: direction,
      });
    }
  };

  const handleShowNewFormModal = () => {
    if (selectedLocationSet?.length < 1) {
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: () => {
                    onClose();
                  },
                  classNames: "btn btn-primary",
                },
              ]}
              message={translate("forms.select_location_warning")}
            />
          );
        },
      });
      return false;
    }

    setShowNewFormModal(true);
  };
  
  const handleExport = (type, exportAll = false) => {
    let url = `/forms/export?file_format=${type}&timezone=${getTimezone()}`;

    if (exportAll) {
      url += `&page=1&size=10000`;
    } else {
      url += `&page=${currentPage}&size=${perPage}`;
    }

    if (query) url += `&search=${query}`;

    API.postData(url, {
      project_id: project.id,
      site_set_id: siteSetId,
      site_set_detail_id: siteSetDetailId,
      attributes: selectedAttributes.map((item) => item.id),
      created_bys: selectedFilters.created_by?.value
        ? [selectedFilters.created_by.value]
        : [],
      statuses: selectedFilters.form_status?.length
        ? selectedFilters.form_status?.map((item) => item.value)
        : [],

      advanced_filters: {
        form_no: advancedFilters.formNo.value !== "" ? advancedFilters.formNo : undefined,
        form_name: advancedFilters.formName.value !== "" ? advancedFilters.formName : undefined,
        statuses: advancedFilters.status.value.length > 0 ? { condition: advancedFilters.status.condition, value: advancedFilters.status.value.map((item) => item.value) } : undefined,
        created_by_companies: advancedFilters.createdByCompanies.value.length > 0 ? { condition: advancedFilters.createdByCompanies.condition, value: advancedFilters.createdByCompanies.value.map((item) => item.value) } : undefined,
        created_by_users: advancedFilters.createdByUsers.value.length > 0 ? { condition: advancedFilters.createdByUsers.condition, value: advancedFilters.createdByUsers.value.map((item) => item.value) } : undefined,
        created_at: checkDateFilterForBackend(advancedFilters.createdAt) !== undefined ? {condition: advancedFilters.createdAt.condition, ...checkDateFilterForBackend(advancedFilters.createdAt) } : undefined,
        updated_at: checkDateFilterForBackend(advancedFilters.updatedAt) !== undefined ? {condition: advancedFilters.updatedAt.condition, ...checkDateFilterForBackend(advancedFilters.updatedAt) } : undefined,
      }

      // last_id: 0,
      // templates: [],
    }).then(({ data: res }) => {
      toastr.success(translate("forms.export_success"));
    });
  };

  return (
    <div className="Forms">
      <div className="container-fluid">
        <div className="row my-4">
          <div className="col-12 col-md-6">
            <Breadcrumbs
              data={[
                {
                  id: 0,
                  customTitle: project?.name || (
                    <Translate id={auth.projects.name} />
                  ),
                  route: "auth.projects",
                },
                { id: 1, customTitle: translate("page_titles.forms") },
              ]}
            />
          </div>
          <div className="col-12 col-md-6 d-flex align-items-center justify-content-end">
            <Button
              className="btn-secondary mx-2 text-uppercase"
              onClick={() => setShowTaskModal(true)}
              disabled={selectedForms.length < 1 ? true : false}
            >
              {translate("forms.batch_send")}
            </Button>
            <Button
              className="btn-secondary mx-2"
              onClick={() => setShowExportModal(true)}
            >
              <div className="d-flex align-items-center">
                <AiOutlineExport className="fs-5 me-2" />
                {translate("export_as")}
              </div>
            </Button>
            <Button className="btn-secondary" onClick={() => setShow(true)}>
              <div className="d-flex align-items-center">
                <BiFilterAlt className="fs-5 me-2" />
                {translate("filter")}
              </div>
            </Button>

            {can.doThat("form:create") && (
              <Button
                className="btn-primary ms-2"
                onClick={handleShowNewFormModal}
                disabled={!isEnableNewForm ? true : false}
              >
                <div className="d-flex align-items-center">
                  <AiOutlinePlusCircle className="fs-4 me-2" />
                  {translate("forms.create_new_form")}
                </div>
              </Button>
            )}
            {can.doThat("form:create") && getEnv("REACT_APP_ENABLE_AUTOMATIC_FORM_CREATION") === "true" && (
              <Button
                className="btn-secondary ms-2"
                onClick={() => setShowAutomaticFormCreationModal(true)}
              >
                <div className="d-flex align-items-center">
                  <AiOutlinePlusCircle className="fs-4 me-2" />
                  {translate("forms.automatic_form_creation")}
                </div>
              </Button>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-0 col-md-2"></div>
          <div className="col-12 col-md-10 mb-2">
            {selectedAttributes?.map((attribute, index) => (
              <span
                className="badge bg-secondary text-center py-1 me-3"
                key={index}
              >
                <IoMdClose
                  className="fw-bold cursor--pointer fill-white fs-5"
                  onClick={() => removeSelectedAttribute(attribute.id)}
                />
                {attribute.name}
              </span>
            ))}
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-2 ps-0">
            <div className="w-100 d-flex align-items-center justify-content-center px-2 py-2 me-0 me-md-4 bg-white shadow-sm rounded border-gray border">
              <SelectBox
                options={allSiteSets?.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                onChange={(e) => allSiteSetsHandler(e)}
                value={locationSetRoot}
                placeholder={translate("forms.place-holders.select-location")}
                height="42px"
                width="100%"
              />
            </div>
          </div>
          <div className="col-12 col-md-10 py-2 bg-white shadow-sm rounded border-gray border">
            <div className="d-flex flex-wrap align-items-center">
              {locationSetRoot ? (
                <div
                  className="d-flex align-items-center py-1"
                  onClick={() => selectedLocationSetHandler(0)}
                  role="button"
                >
                  <MdLocationOn className="fs-2 text-orange" />
                  <p className="mb-0 ps-1 pe-2">{locationSetRoot.label}</p>
                </div>
              ) : null}

              {selectedLocationSet?.map((location, index) => (
                <div
                  className="d-flex flex-wrap align-items-center"
                  key={index}
                  onClick={() => selectedLocationSetHandler(index)}
                  role="button"
                >
                  <MdOutlineArrowForwardIos />
                  <p className="mb-0 px-2">{location.name}</p>
                </div>
              ))}

              {viewNext && (
                <div className="d-flex flex-wrap align-items-center">
                  <MdOutlineArrowForwardIos className="me-2" />
                  <SelectBox
                    width="250px"
                    options={locationSet?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                    isMulti={false}
                    value={[]}
                    onChange={(e) => setSiteSetDetailByID(e.value)}
                    placeholder={
                      isLoadingSiteSetDetails || isLoading
                        ? translate("loading")
                        : translate("please_select")
                    }
                    isDisabled={isLoadingSiteSetDetails || isLoading}
                  />
                </div>
              )}

              <div className="fetch-button ms-auto">
                <Button
                  className={
                    (!locationSetRoot && !locationSet?.length) ||
                    isFetched ||
                    isLoading
                      ? `btn-secondary`
                      : `btn-primary`
                  }
                  disabled={
                    (!locationSetRoot && !locationSet?.length) ||
                    isFetched ||
                    isLoading
                  }
                  onClick={() => getForms(perPage, 1)}
                >
                  {translate("forms.list")}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-4 bg-white py-3">
          <div className="col-12 col-md-3">
            <Input
              placeholder={translate("forms.place-holders.search-form-name")}
              onChange={handleSearch}
              iconNode={<AiOutlineSearch className="fs-5 me-2 mt-2" />}
            />
          </div>
          <div className="col-12 col-md-3 d-flex align-items-center mb-3">
            <SelectBox
              options={
                filters?.form_status?.map((item) => ({
                  value: item,
                  label: item,
                })) || []
              }
              value={selectedFilters.form_status}
              placeholder={translate("forms.place-holders.select-status")}
              onChange={(selectedStatus) =>
                setSelectedFilters({
                  ...selectedFilters,
                  form_status: selectedStatus,
                })
              }
              width="100%"
              isClearable={true}
              isMulti={true}
              closeMenuOnSelect={false}
            />
          </div>
          <div className="col-12 col-md-3">
            <SelectBox
              options={
                filters?.created_by?.map((item) => ({
                  value: item.id,
                  label: item.name,
                })) || []
              }
              value={selectedFilters.created_by}
              onChange={(selectedCreatedBy) =>
                setSelectedFilters({
                  ...selectedFilters,
                  created_by: selectedCreatedBy,
                })
              }
              placeholder={translate("forms.place-holders.select-created-by")}
              width="100%"
              isClearable={true}
            />
          </div>
          <div className="col-12 px-0 shadow-sm rounded">
            <DataTable
              highlightOnHover={true}
              columns={columns}
              data={formsList}
              progressComponent={
                <Loading
                  classNames="mt-3"
                  text={translate("loading-uppercase") + `...`}
                />
              }
              progressPending={isLoading}
              pagination
              onRowClicked={(row) =>
                window.open(auth.formDetails.link.replace(":id", row.id))
              }
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              pointerOnHover
              selectableRows={true}
              onSelectedRowsChange={(state) => {
                if (state.selectedCount === 1 && selectedFormsLocation === "") {
                  setSelectedFormsLocation(state.selectedRows[0].location);
                } else if (state.selectedCount === 0) {
                  setSelectedFormsLocation("");
                }

                setSelectedForms(state.selectedRows);
              }}
              selectableRowDisabled={(row) =>
                (selectedFormsLocation !== "" &&
                  row.location !== selectedFormsLocation) ||
                row.locked
              }
              selectableRowsNoSelectAll={true}
              clearSelectedRows={clearSelectedForms}
              paginationResetDefaultPage={resetPaginationToggle}
              onSort={handleSort}
              sortServer
              noDataComponent={
                (!locationSetRoot && !locationSet?.length) || !firstFetch ? (
                  <h5 className="my-4 text-muted h2">
                    {translate("select_a_location_alert")}
                  </h5>
                ) : null
              }
            />
          </div>
        </div>

        <Modal
          title={translate("alert")}
          modaSize="lg"
          show={isProjectControl}
          setShow={setIsProjectControl}
        >
          <p className="fs-5 text-center mt-2">
            {translate("select_a_project_alert")}
          </p>
        </Modal>

        <Modal
          title={translate("form_templates")}
          modalSize="lg"
          show={showNewFormModal}
          setShow={setShowNewFormModal}
        >
          <div className="form-templates-holder w-100 overflow-auto d-flex flex-column align-items-center">
            {isLoadingFormTemplates ? (
              <Loading
                classNames="mt-3"
                text={translate("loading-uppercase") + `...`}
              />
            ) : formTemplates?.map((item, index) => (
              <Link
                to={auth.newForm.link.replace(":template", item.id)}
                className="cursor--pointer w-100 border-top border-1 ps-2 py-3 hover-green"
                key={index}
                onClick={() => {
                  localStorage.setItem(
                    "new_form_location_sets",
                    JSON.stringify([
                      {
                        id: locationSetRoot.value,
                        name: locationSetRoot.label,
                      },
                      ...selectedLocationSet.map((item) => ({
                        id: item.id,
                        name: item.name,
                      })),
                    ])
                  );
                }}
              >
                <p className="mb-0">{item.name}</p>
              </Link>
            ))}
          </div>
        </Modal>

        <AutomaticFormCreationModal
          translate={translate}
          show={showAutomaticFormCreationModal}
          setShow={setShowAutomaticFormCreationModal}
          project={project}
        />

        <Modal
          title={translate("filter")}
          positiveButtonNode={translate("apply")}
          negativeButtonNode={translate("cancel")}
          positiveButtonHandler={() => handleModalApply()}
          negativeButtonHandler={() => handleModalClose()}
          modalSize="xl"
          disableButtons={checkAdvancedFilters()}
          show={show}
          setShow={setShow}
        >
          <>
            {filters?.attributes?.map((item, index) => (
              <div className="row mb-3" key={index}>
                <div className="col-12 text-center">
                  <h3 className="fw-bold text-uppercase">{item.name}</h3>
                </div>
                <div className="col-12 mb-3 text-center">
                  {item.details.map((detail, index2) => (
                    <Button
                      className={`me-2 mb-2 ${
                        selectedAttributesControl(detail.id)
                          ? "btn-success"
                          : ""
                      }`}
                      onClick={() => handleAttributesClick(detail)}
                      key={index2}
                    >
                      {detail.name}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
            <div className="col-12 mb-3 p-1 rounded-1">
              {/* Header Row */}
              <div className="row mb-1">
                <div className="col-12 col-md-3 mb-1">
                  <span className="fw-bold text-dark border-bottom border-2 pb-1">
                    {translate("forms.headers.field_name")}
                  </span>
                </div>
                <div className="col-12 col-md-3 mb-1">
                  <span className="fw-bold text-dark border-bottom border-2 pb-1">
                    {translate("forms.headers.operator")}
                  </span>
                </div>
                <div className="col-12 col-md-6 mb-1">
                  <span className="fw-bold text-dark border-bottom border-2 pb-1">
                    {translate("forms.headers.input")}
                  </span>
                </div>
              </div>

              {/* Filter Fields Rows */}
              {filterFields.map((field, index) => (
                <div key={field.key} className="row mb-2 p-2">
                  {/* Field Label Column */}
                  <div className="col-12 col-md-3 d-flex justify-content-start align-items-center border-start border-2">
                    <span className="fw-bold text-gray text-center py-2">
                      {field.label} :
                    </span>
                  </div>

                  {/* Conditions Column */}
                  <div className="col-12 col-md-3 flex justify-content-center align-items-center">
                    <Conditions
                      translate={translate}
                      conditions={field.conditions}
                      value={advancedFilters[field.key].condition}
                      onChange={(condition) => 
                        setAdvancedFilters(prev => ({
                          ...prev,
                          [field.key]: { 
                            ...prev[field.key], 
                            condition,
                            ...(field.type === "datetime" && { start_date: "", end_date: "" })
                          }
                        }))
                      }
                    />
                  </div>

                  {/* Input Column */}
                  {field.type === "text" && (
                    <div className="col-12 col-md-6">
                      <Input
                        placeholder={translate(field.placeholder)}
                        value={advancedFilters[field.key].value}
                        onChange={(e) => 
                          setAdvancedFilters(prev => ({
                            ...prev,
                            [field.key]: { ...prev[field.key], value: e.target.value }
                          }))
                        }
                      />
                    </div>
                  )}

                  {field.type === "select" && (
                    <div className="col-12 col-md-6">
                      <SelectBox
                        options={filters?.[field.options]?.map((item) => 
                          field.options === "form_status" 
                            ? { value: item, label: item }
                            : { value: item.id, label: item.name }
                        )}
                        value={advancedFilters[field.key].value}
                        onChange={(selectedValue) => 
                          setAdvancedFilters(prev => ({
                            ...prev,
                            [field.key]: { ...prev[field.key], value: selectedValue }
                          }))
                        }
                        placeholder={translate(field.placeholder)}
                        isMulti={field.isMulti}
                      />
                    </div>
                  )}

                  {field.type === "datetime" && (
                    <>
                      {/* Single date input for equal, before, after conditions */}
                      {(advancedFilters[field.key].condition === "equal" || 
                        advancedFilters[field.key].condition === "before" || 
                        advancedFilters[field.key].condition === "after") && (
                          <div className="col-12 col-md-6">
                            <DateTimeLayout
                              language={language}
                              type="DD/MM/YY HH:MM:SS"
                              disabled={false}
                              value={advancedFilters[field.key].start_date}
                              onChange={(value) => 
                                setAdvancedFilters(prev => ({
                                  ...prev,
                                  [field.key]: { ...prev[field.key], start_date: value }
                                }))
                              }
                            />
                          </div>
                      )}

                      {/* Date range inputs for in_between condition */}
                      {advancedFilters[field.key].condition === "in_between" && (
                        <>
                          <div className="col-12 col-md-3 mb-2">
                            <label className="form-label text-muted small">{translate("forms.headers.start_date")}</label>
                            <DateTimeLayout
                              language={language}
                              type="DD/MM/YY HH:MM:SS"
                              disabled={false}
                              value={advancedFilters[field.key].start_date}
                              onChange={(value) => 
                                setAdvancedFilters(prev => ({
                                  ...prev,
                                  [field.key]: { ...prev[field.key], start_date: value }
                                }))
                              }
                            />
                          </div>
                          <div className="col-12 col-md-3 mb-2">
                            <label className="form-label text-muted small">{translate("forms.headers.end_date")}</label>
                            <DateTimeLayout
                              language={language}
                              type="DD/MM/YY HH:MM:SS"
                              minValue={advancedFilters[field.key].start_date !== "" ? getUnixTime(addDays(new Date(advancedFilters[field.key].start_date * 1000), 1)) : undefined}
                              disabled={false}
                              value={advancedFilters[field.key].end_date}
                              onChange={(value) => 
                                setAdvancedFilters(prev => ({
                                  ...prev,
                                  [field.key]: { ...prev[field.key], end_date: value }
                                }))
                              }
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        </Modal>

        <ExportModal
          show={showExportModal}
          setShow={setShowExportModal}
          onExport={handleExport}
          translate={translate}
        />

        <NewTaskAssignmentModal
          show={showTaskModal}
          hideModal={(action) => {
            if (action === "success") {
              toastr.success(translate("forms.batch_send_success"));
              setClearSelectedForms(!clearSelectedForms);
              setSelectedForms([]);
              setShowTaskModal(false);
              getForms(perPage, currentPage);
            } else if (typeof action === "object") {
              toastr.error(
                translate("forms.batch_send_failed", {
                  forms: action
                    .reduce((acc, cur) => {
                      const { formName, no } = selectedForms.find(
                        (item) => item.id === cur
                      );

                      return formName && no
                        ? acc + `${no} - ${formName}<br/>`
                        : acc;
                    }, "")
                    .slice(0, -2),
                })
              );
            } else {
              setShowTaskModal(false);
            }
          }}
          users={taskModalData?.users}
          companies={taskModalData?.companies}
          groups={taskModalData?.groups}
          forms={selectedForms}
          translate={translate}
        />
      </div>
    </div>
  );
};

export default Forms;
